import React, { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
} from "@mui/material"
import { PersonAdd, LocationOn, RateReview } from "@mui/icons-material"
import { fetchRecentSignups, fetchAllBathroomsAdmin, fetchAllReviews } from "../services/adminService"
import { format } from "date-fns"

function AdminActivity() {
  const [tabValue, setTabValue] = useState(0)
  const [recentUsers, setRecentUsers] = useState([])
  const [recentBathrooms, setRecentBathrooms] = useState([])
  const [recentReviews, setRecentReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadActivityData()
  }, [])

  const loadActivityData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [users, bathrooms, reviews] = await Promise.all([fetchRecentSignups(50), fetchAllBathroomsAdmin(), fetchAllReviews()])

      setRecentUsers(users)
      setRecentBathrooms(bathrooms.slice(0, 50))
      setRecentReviews(reviews.slice(0, 50))
      console.log("Recent reviews with user data:", reviews.slice(0, 5))
    } catch (err) {
      console.error("Error loading activity data:", err)
      setError("Failed to load activity data. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          📈 Activity Feed
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Recent platform activity and user engagement
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab label={`New Users (${recentUsers.length})`} icon={<PersonAdd />} iconPosition="start" />
          <Tab label={`New Bathrooms (${recentBathrooms.length})`} icon={<LocationOn />} iconPosition="start" />
          <Tab label={`Recent Reviews (${recentReviews.length})`} icon={<RateReview />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Tab Panels */}
      <Card>
        <CardContent>
          {/* New Users Tab */}
          {tabValue === 0 && (
            <List>
              {recentUsers.map((user, index) => (
                <React.Fragment key={user.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar src={user.photoURL} alt={user.displayName || user.email}>
                        {(user.displayName || user.email || "?")[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {user.displayName || user.email || "Unknown User"}
                          </Typography>
                          {user.emailVerified && <Chip label="Verified" size="small" color="success" />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined {user.createdAt ? format(user.createdAt, "MMM d, yyyy 'at' h:mm a") : "N/A"}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentUsers.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* New Bathrooms Tab */}
          {tabValue === 1 && (
            <List>
              {recentBathrooms.map((bathroom, index) => (
                <React.Fragment key={`${bathroom.locationId}/${bathroom.id}`}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        <LocationOn />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {bathroom.name || bathroom.locationName || "Unnamed Bathroom"}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {bathroom.address || "No address"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Added {bathroom.createdAt ? format(bathroom.createdAt, "MMM d, yyyy 'at' h:mm a") : "N/A"}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentBathrooms.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Recent Reviews Tab */}
          {tabValue === 2 && (
            <List>
              {recentReviews.map((review, index) => (
                <React.Fragment key={`${review.bathroomId}-${index}`}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "info.main" }}>
                        <RateReview />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {review.userName || "Anonymous"}
                          </Typography>
                          {review.ratings?.overall && <Chip label={`⭐ ${review.ratings.overall.toFixed(1)}`} size="small" color="primary" />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Reviewed: {review.bathroomName}
                          </Typography>
                          {review.comment && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
                              "{review.comment.substring(0, 100)}
                              {review.comment.length > 100 ? "..." : ""}"
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {review.timestamp ? format(review.timestamp, "MMM d, yyyy 'at' h:mm a") : "N/A"}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentReviews.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Container>
  )
}

export default AdminActivity
